"use client";

import React from "react";

import { useOrgContext } from "@/lib/context/OrgContext";

interface OrgGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}

/**
 * OrgGuard component that only renders children when org is ready
 * Use this to wrap components that need org data
 */
export function OrgGuard({
  children,
  fallback = <OrgLoadingFallback />,
  errorFallback = <OrgErrorFallback />,
}: OrgGuardProps) {
  const { org, loading, error } = useOrgContext();

  if (loading) {
    return <>{fallback}</>;
  }

  if (error || !org) {
    return <>{errorFallback}</>;
  }

  return <>{children}</>;
}

/**
 * Default loading fallback
 */
function OrgLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="flex flex-col items-center gap-2">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    </div>
  );
}

/**
 * Default error fallback
 */
function OrgErrorFallback() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-red-800 mb-2">
          Organization Error
        </h2>
        <p className="text-red-600 mb-4">
          Unable to load organization settings.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
